import React, {useState, useEffect, useMemo} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {
    Container,
    TextField,
    Button,
    IconButton,
    Typography,
    Paper,
    Snackbar,
    Alert,
    Box,
    Link,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {QRCodeSVG} from 'qrcode.react';
import axiosInstance from '../AxiosInstance';
import {createMfaBearerClient} from '../mfaClient';
import {useAuth} from '../AuthContext';

function MfaSetup() {
    const navigate = useNavigate();
    const location = useLocation();
    const {applyAccessToken} = useAuth();
    const enrolToken = location.state?.enrolToken;

    const api = useMemo(
        () => (enrolToken ? createMfaBearerClient(enrolToken) : axiosInstance),
        [enrolToken],
    );

    const [authenticatorUri, setAuthenticatorUri] = useState('');
    const [sharedKey, setSharedKey] = useState('');
    const [code, setCode] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [errorOpen, setErrorOpen] = useState(false);
    const [copyOpen, setCopyOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const {data} = await api.post('/mfa/enroll/start');
                if (!cancelled) {
                    setAuthenticatorUri(data.authenticatorUri || '');
                    setSharedKey(data.sharedKey || '');
                }
            } catch (err) {
                if (!cancelled) {
                    setErrorMessage(err.response?.data?.message || 'Could not start setup.');
                    setErrorOpen(true);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [api]);

    const handleConfirm = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrorOpen(false);
        try {
            const {data} = await api.post('/mfa/enroll/confirm', {code: code.trim()});
            setRecoveryCodes(data.recoveryCodes || []);
            if (data.token) {
                applyAccessToken(data.token);
            }
        } catch (err) {
            setErrorMessage(err.response?.data?.message || 'Invalid code.');
            setErrorOpen(true);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDone = () => {
        if (enrolToken) {
            navigate('/', {replace: true});
        } else {
            navigate('/settings/profile', {replace: true});
        }
    };

    const handleCopyRecoveryCodes = async () => {
        if (!recoveryCodes || recoveryCodes.length === 0) {
            return;
        }

        try {
            await navigator.clipboard.writeText(recoveryCodes.join('\n'));
            setCopyOpen(true);
        } catch {
            setErrorMessage('Could not copy recovery codes. Please copy them manually.');
            setErrorOpen(true);
        }
    };

    if (recoveryCodes) {
        return (
            <Container maxWidth="sm" className="component-container" sx={{mt: 4}}>
                <Paper elevation={3} sx={{p: 3}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <Typography variant="h6" gutterBottom>
                            Save your recovery codes
                        </Typography>
                        <IconButton aria-label="Copy recovery codes" onClick={handleCopyRecoveryCodes}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        Store these codes in a safe place. You will only see these recovery codes once, and each can be used once if you lose your authenticator device.
                    </Typography>
                    <Box component="ul" sx={{pl: 3, fontFamily: 'monospace'}}>
                        {recoveryCodes.map((c) => (
                            <li key={c}>{c}</li>
                        ))}
                    </Box>
                    <Button variant="contained" color="primary" fullWidth sx={{mt: 2}} onClick={handleDone}>
                        Done
                    </Button>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" className="component-container" sx={{mt: 4}}>
            <Paper elevation={3} sx={{p: 3}}>
                <Typography component="h1" variant="h5" gutterBottom>
                    Set up authenticator
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    Scan this QR code with your authenticator app (Microsoft Authenticator, Google Authenticator, etc.), then enter the 6-digit code.
                </Typography>
                {loading ? (
                    <Typography>Loading…</Typography>
                ) : (
                    <>
                        {authenticatorUri ? (
                            <Box sx={{display: 'flex', justifyContent: 'center', mb: 2}}>
                                <QRCodeSVG value={authenticatorUri} size={200} />
                            </Box>
                        ) : null}
                        {sharedKey ? (
                            <Typography variant="caption" display="block" sx={{mb: 2, wordBreak: 'break-all'}}>
                                Manual key: {sharedKey}
                            </Typography>
                        ) : null}
                        <form onSubmit={handleConfirm}>
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                label="6-digit code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                autoComplete="one-time-code"
                            />
                            <Button type="submit" fullWidth variant="contained" color="primary" sx={{mt: 2}} disabled={submitting || loading}>
                                {submitting ? 'Verifying…' : 'Confirm and enable'}
                            </Button>
                        </form>
                    </>
                )}
                <Box sx={{mt: 2, textAlign: 'center'}}>
                    {enrolToken ? (
                        <Link component="button" type="button" variant="body2" onClick={() => navigate('/login')}>
                            Cancel
                        </Link>
                    ) : (
                        <Link component="button" type="button" variant="body2" onClick={() => navigate('/settings/profile')}>
                            Cancel
                        </Link>
                    )}
                </Box>
            </Paper>
            <Snackbar open={errorOpen} autoHideDuration={6000} onClose={() => setErrorOpen(false)} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
                <Alert severity="error" onClose={() => setErrorOpen(false)} sx={{width: '100%'}}>
                    {errorMessage}
                </Alert>
            </Snackbar>
            <Snackbar open={copyOpen} autoHideDuration={3000} onClose={() => setCopyOpen(false)} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
                <Alert severity="success" onClose={() => setCopyOpen(false)} sx={{width: '100%'}}>
                    Recovery codes copied to clipboard.
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default MfaSetup;
